import React, { useState } from "react";
import { Link } from "react-router-dom";
import { memberFetch } from "../../lib/memberApi";
import { toast } from "sonner";
import { ShieldAlert, AlertCircle, Sparkles } from "lucide-react";

export default function OneOnOneStatusBanner({ me, refreshMe }) {
  const [busy, setBusy] = useState(false);
  const req = me?.latest_request;
  const ppm = me?.pending_payment_method || "";
  const squareTier = ppm.startsWith("square_") ? ppm.replace("square_", "") : null;
  const squareLinks = {
    basic: "https://square.link/u/7mrh2XHW",
    accountability: "https://square.link/u/ClTkxdX3",
    advance: "https://square.link/u/ivjkNVM7",
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await memberFetch("/member/request-1on1/dismiss", { method: "POST", body: JSON.stringify({}) });
      await refreshMe();
    } catch (e) {
      toast.error(e.message || "Could not dismiss");
    } finally {
      setBusy(false);
    }
  };

  if (squareTier && !me.active && squareLinks[squareTier]) {
    const label = squareTier.charAt(0).toUpperCase() + squareTier.slice(1);
    return (
      <div className="mb-8 border border-amber-500/60 bg-amber-500/5 px-5 py-4 flex items-start gap-3" data-testid="square-pending-banner">
        <ShieldAlert className="text-amber-400 shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-1">{label} Membership · Awaiting Square Payment</div>
          <div className="text-sm text-zinc-200 mb-3">We&apos;re waiting on your Square payment to clear. <span className="text-zinc-400">Once Davon confirms, your access flips on for 30 days and you&apos;ll get a confirmation email. Didn&apos;t finish checkout?</span></div>
          <a href={squareLinks[squareTier]} target="_blank" rel="noopener noreferrer" data-testid="square-link-retry" className="inline-block text-xs font-bold tracking-[0.2em] uppercase bg-[#E5B43A] text-black px-3 py-2 hover:bg-white hover:text-black transition-colors">Pay with Square</a>
        </div>
      </div>
    );
  }

  if (!req) return null;

  if (req.status === "pending") {
    return (
      <div className="mb-8 border border-amber-500/60 bg-amber-500/5 px-5 py-4 flex items-start gap-3" data-testid="oneonone-pending-banner">
        <ShieldAlert className="text-amber-400 shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-1">1-on-1 Application · Waiting on Approval</div>
          <div className="text-sm text-zinc-200">Your application for 1-on-1 coaching is in. <span className="text-zinc-400">Davon reviews requests personally — you&apos;ll get an email the moment it&apos;s decided. Hang tight.</span></div>
        </div>
      </div>
    );
  }
  if (req.status === "denied") {
    return (
      <div className="mb-8 border border-[#E5B43A]/60 bg-[#E5B43A]/5 px-5 py-4 flex items-start gap-3" data-testid="oneonone-denied-banner">
        <AlertCircle className="text-[#E5B43A] shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-1">1-on-1 Application · Not Accepted</div>
          <div className="text-sm text-zinc-200 mb-3">Davon couldn&apos;t approve your 1-on-1 application right now. <span className="text-zinc-400">You can still pick any paid tier — Basic, Accountability, or Advance — and get full feature access today.</span></div>
          <div className="flex gap-3">
            <Link to="/membership" data-testid="denied-pick-tier" className="text-xs font-bold tracking-[0.2em] uppercase bg-[#E5B43A] text-black px-3 py-2 hover:bg-white hover:text-black transition-colors">View Tiers</Link>
            <button onClick={dismiss} disabled={busy} data-testid="dismiss-denied" className="text-xs font-bold tracking-[0.2em] uppercase border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors disabled:opacity-50">{busy ? "..." : "Dismiss"}</button>
          </div>
        </div>
      </div>
    );
  }
  if (req.status === "approved") {
    return (
      <div className="mb-8 border border-emerald-500/60 bg-emerald-500/5 px-5 py-4 flex items-start gap-3" data-testid="oneonone-approved-banner">
        <Sparkles className="text-emerald-400 shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400 mb-1">1-on-1 Coaching · Approved</div>
          <div className="text-sm text-zinc-200 mb-3">You&apos;re in. Full access unlocked — workouts, meal plans, billing, progress. <span className="text-zinc-400">Davon will reach out directly to set up your text line.</span></div>
          <button onClick={dismiss} disabled={busy} data-testid="dismiss-approved" className="text-xs font-bold tracking-[0.2em] uppercase border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-emerald-400 hover:text-emerald-400 transition-colors disabled:opacity-50">{busy ? "..." : "Got it"}</button>
        </div>
      </div>
    );
  }
  return null;
}
